/**
 * German copy for the refusals the **Automatisierungen** screen can be answered with.
 *
 * It exists for the same reason `event-creation-errors.ts` does, and follows the same two rules from the style
 * guide's *"Surfacing an API error"* section: branch on `ApiError.code`, and never render the server's English
 * `description`, which is developer-facing and free to be reworded by `ErrorCatalog` at any time.
 *
 * Most failures on this controller still arrive as a bare status with no `ApiError` body at all, so the
 * per-call-site fallback is the whole message on those paths — pass one, always.
 *
 * Addressing is the formal `Sie`.
 */

import {readErrorCode} from '../../_auth/two-factor/second-factor-errors';

/** The codes this screen recognises, spelled exactly as the server's catalog registers them. */
export const AutomationErrorCodes = {
  /**
   * The chosen target event is a **virtual** one (#664).
   *
   * A price automation rewrites price profiles and runs on an event's registers; a virtual event has neither,
   * so the server refuses the group with a 400 and `details.eventId`. The picker already offers Kassen-Events
   * only — this is the message for the ways around it: a stale list, a second tab, a direct call.
   */
  VirtualNotAutomatable: 'E_EVENT_VIRTUAL_NOT_AUTOMATABLE'
} as const;

/** `ApiError.code` → German sentence. Each names the rule *and* the way out of it. */
const AUTOMATION_MESSAGES: Readonly<Record<string, string>> = {
  [AutomationErrorCodes.VirtualNotAutomatable]:
    'Ein virtuelles Event kann keine Preisautomatisierung steuern. Bitte wählen Sie ein Kassen-Event.'
};

/** Shown for a refusal this build has no sentence for, so no English ever reaches the screen. */
export const AUTOMATION_ERROR_FALLBACK =
  'Die Aktion war nicht möglich. Bitte versuchen Sie es erneut.';

/**
 * The German sentence for a failed automation call.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the sentence for a failure carrying no recognised code. **Always pass one** — most refusals
 *                 on this controller are a bare status, so this is usually the entire message.
 */
export function describeAutomationError(err: unknown,
                                        fallback: string = AUTOMATION_ERROR_FALLBACK): string {
  const code = readErrorCode(err);
  return (code && AUTOMATION_MESSAGES[code]) || fallback;
}
